import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { isCurrentCompanyMembersRequest } from "@src/store/members/members-slice-helper";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { fetchCompanyMemberDetails, fetchCompanyMembers, removeMemberFromCompany, setMemberPermissionsInCompany, updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
/**
 * Creates an entity adapter to store a map with all the members that belong to the company.
 * Since we can also fetch teams as member type, they will also be stored here.
 */
export const membersAdapter = createEntityAdapter({
  selectId: member => FaroCoreApiUtils.Member.getMemberId(member)
});
const initialState = {
  ...membersAdapter.getInitialState(),
  fetched: {
    hasFetchedWorkspaceMembers: false
  },
  fetching: {
    isFetchingCompanyMembers: false,
    isFetchingCompanyMemberDetails: false,
    isRemovingCompanyMember: false,
    isUpdatingMemberRole: false,
    hasFetchingCompanyMembersFailed: false
  }
};

/**
 * Update a member role in workspace store.
 *
 * The request sets the role assigned to the member, so a member listed with that role as well
 * (`userRole`) gets it there too. Otherwise the assigned role would stay the one they had when the
 * members were fetched, e.g. admin after they were made a viewer to take their seat away.
 */
function updateMemberRoleInMemberStore(state, result) {
  const member = membersAdapter.getSelectors().selectById(state, result.identity);

  // If the member is in the store update the member role
  if (member) {
    membersAdapter.updateOne(state, {
      id: FaroCoreApiUtils.Member.getMemberId(member),
      changes: {
        role: result.role,
        ...("userRole" in member ? {
          userRole: result.role
        } : {})
      }
    });
  }
}

/**
 * Merges permission lists by permission name, where a later list overrides an earlier one.
 *
 * @param permissionLists The permission lists to merge, from the oldest to the newest
 * @returns The merged permissions, one entry per permission name
 */
function mergePermissions(...permissionLists) {
  const permissionsByName = new Map();
  for (const permission of permissionLists.flatMap(list => list ?? [])) {
    permissionsByName.set(permission.name, permission);
  }
  return [...permissionsByName.values()];
}

/**
 * Updates the permissions of a member in the store after they were set in the backend.
 *
 * The response does not necessarily list every permission of the member, e.g. it can leave out the
 * one that was just set. Replacing the permissions with the response would then drop that change
 * until the members are fetched again, so the stored, the requested and the returned permissions
 * are merged instead.
 */
function updateMemberPermissionsInMemberStore(state, userIdentity, requestedPermissions, returnedPermissions) {
  // A member that did not accept their invite yet is stored by their ID but identified by their
  // email, so they are looked up by their identity as well
  const member = membersAdapter.getSelectors().selectById(state, userIdentity) ?? Object.values(state.entities).find(storedMember => storedMember?.identity === userIdentity);
  if (!member) {
    return;
  }
  membersAdapter.updateOne(state, {
    id: FaroCoreApiUtils.Member.getMemberId(member),
    changes: {
      permissions: mergePermissions("permissions" in member ? member.permissions : undefined, requestedPermissions, returnedPermissions)
    }
  });
}

/**
 * Ends a request to fetch the company members. The outcome of a request that the members were
 * invalidated or reset during is ignored, since it can miss what changed meanwhile or belong to
 * another workspace, and the loading only ends if no request was started since.
 *
 * @param state store state
 * @param requestId The ID of the request that ended
 * @returns Whether the outcome of the request counts
 */
function endCompanyMembersRequest(state, requestId) {
  const isCurrent = isCurrentCompanyMembersRequest(state, requestId);
  if (isCurrent) {
    state.fetching.companyMembersRequestId = undefined;
  }
  if (state.fetching.companyMembersRequestId === undefined) {
    state.fetching.isFetchingCompanyMembers = false;
  }
  return isCurrent;
}

/**
 * Slice to access state of loaded company members
 */
const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    /**
     * Accepts a single member entity and adds or replaces it.
     *
     * @param state store state
     * @param entity Project to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: membersAdapter.setOne,
    /**
     * Adds a member, unless they are stored already with the same identity or email. Unlike setOne,
     * it does not replace what is known about them, e.g. a seat given along their invite, and it
     * does not rely on the ID they are stored by, which a pending member may or may not have.
     *
     * @param state store state
     * @param action The member to add
     */
    addMemberIfMissing(state, action) {
      const {
        identity,
        email
      } = action.payload;
      const isStored = Object.values(state.entities).some(storedMember => !!storedMember && (storedMember.identity === identity || !!email && "email" in storedMember && storedMember.email?.toLowerCase() === email.toLowerCase()));
      if (!isStored) {
        membersAdapter.addOne(state, action.payload);
      }
    },
    /**
     * Marks the workspace members as no longer fetched, so that they are fetched again, e.g. after
     * somebody joined the workspace along another change that does not return all of them. A
     * request that is already on its way can miss that change, so its outcome is ignored and the
     * next fetch is not skipped because of it.
     *
     * @param state store state
     */
    invalidateWorkspaceMembers(state) {
      state.fetched.hasFetchedWorkspaceMembers = false;
      state.fetching.companyMembersRequestId = undefined;
    },
    /**
     * Changes some fields of a stored member, leaving all the others as they are.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    updateMember: membersAdapter.updateOne,
    /**
     * Accepts an array of member entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity Projects to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: membersAdapter.setMany,
    /**
     * Removes all member entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: membersAdapter.removeAll,
    /**
     * Accepts an array of member IDs, and removes each member entity with those IDs if they exist.
     *
     * @param state store state
     * @param entity Project Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: membersAdapter.removeMany,
    /**
     * Accepts a single member IDs, and removes the member entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Project Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: membersAdapter.removeOne,
    resetMembersState: () => initialState
  },
  extraReducers(builder) {
    builder.addCase(fetchCompanyMembers.pending, (state, action) => {
      state.fetching.isFetchingCompanyMembers = true;
      state.fetching.hasFetchingCompanyMembersFailed = false;
      state.fetching.companyMembersRequestId = action.meta.requestId;
    }).addCase(fetchCompanyMembers.fulfilled, (state, action) => {
      if (endCompanyMembersRequest(state, action.meta.requestId)) {
        membersAdapter.upsertMany(state, action.payload);
        state.fetched.hasFetchedWorkspaceMembers = true;
      }
    }).addCase(fetchCompanyMembers.rejected, (state, action) => {
      if (endCompanyMembersRequest(state, action.meta.requestId)) {
        state.fetching.hasFetchingCompanyMembersFailed = true;
      }
    }).addCase(fetchCompanyMemberDetails.pending, state => {
      state.fetching.isFetchingCompanyMemberDetails = true;
    }).addCase(fetchCompanyMemberDetails.fulfilled, (state, action) => {
      membersAdapter.upsertOne(state, action.payload);
      state.fetching.isFetchingCompanyMemberDetails = false;
    }).addCase(fetchCompanyMemberDetails.rejected, state => {
      state.fetching.isFetchingCompanyMemberDetails = false;
    }).addCase(removeMemberFromCompany.pending, state => {
      state.fetching.isRemovingCompanyMember = true;
    }).addCase(removeMemberFromCompany.fulfilled, (state, action) => {
      state.fetching.isRemovingCompanyMember = false;
      membersAdapter.removeOne(state, FaroCoreApiUtils.Member.getMemberId(action.payload));
    }).addCase(removeMemberFromCompany.rejected, state => {
      state.fetching.isRemovingCompanyMember = false;
    }).addCase(updateMemberRoleInWorkspace.pending, state => {
      state.fetching.isUpdatingMemberRole = true;
    }).addCase(updateMemberRoleInWorkspace.fulfilled, (state, action) => {
      updateMemberRoleInMemberStore(state, action.payload);
      state.fetching.isUpdatingMemberRole = false;
    }).addCase(updateMemberRoleInWorkspace.rejected, state => {
      state.fetching.isUpdatingMemberRole = false;
    }).addCase(setMemberPermissionsInCompany.fulfilled, (state, action) => {
      // The member is looked up by the identity the permissions were set for, which is also the
      // key of a registered member in the store, instead of trusting the response with it.
      updateMemberPermissionsInMemberStore(state, action.meta.arg.userIdentity, action.meta.arg.permissions, action.payload.permissions);
    });
  }
});
export const {
  setOne,
  addMemberIfMissing,
  invalidateWorkspaceMembers,
  updateMember,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  resetMembersState
} = membersSlice.actions;
export const membersReducer = membersSlice.reducer;